import { SelectionTreeT } from 'business/definitions';
import { useCallback } from 'react';
import { trackEventBudget } from 'helpers/trackerHelpers';
import {
    PROJECTION_ID,
    PROJECTION_ATTERRISSAGE_STATUS,
    PROJECTION_PLURIANNUAL_STATUS,
} from 'routes/BudgetModule/config/constants';
import { ResourceSoftwareIdField } from 'routes/BudgetModule/config/types';
import { parseNumericInputOrDefault } from 'helpers/UIHelpers';
import {
    ProjectionT,
    ProjectionsDimensionsMapping,
    VentilationsLabelMapping,
    EmptyProjectionDetails,
    ProjectionVentilation,
    AbstractProjectionDetailsT,
    AtterrissageProjectionDetails,
    PluriannualProjectionDetails,
    ProjectionVentilationData,
} from './types';
import { IRowNode } from 'ag-grid-community';

const getNameMapping = ({
    projectionDetails,
    nameField,
    idField,
}: {
    projectionDetails: AbstractProjectionDetailsT[];
    nameField: ProjectionVentilation.Labels;
    idField: ProjectionVentilation.SoftwareIds;
}): Record<string, string> =>
    projectionDetails.reduce((mapping, detail) => {
        const softwareId = detail[idField];
        const name = detail[nameField];
        if (!softwareId) return mapping;
        return Object.assign(mapping, {
            [softwareId as string]: name,
        });
    }, {});

const getDimensionMapping = (
    projectionDetails: AbstractProjectionDetailsT[],
    dimensionByNameField: ProjectionVentilation.Labels,
    dimensionBySoftwareIdField: ProjectionVentilation.SoftwareIds,
): ProjectionsDimensionsMapping => {
    return {
        [dimensionBySoftwareIdField]: {
            mapping: getNameMapping({
                projectionDetails,
                nameField: dimensionByNameField,
                idField: dimensionBySoftwareIdField,
            }),
        },
    };
};

const addProjectionFieldsToProjectionDetails = ({
    projectionDetail,
    projectionsByResourceId,
    resourceSoftwareIdField,
}: {
    projectionDetail: EmptyProjectionDetails.FromEndpoint;
    projectionsByResourceId: Record<string, ProjectionT>;
    resourceSoftwareIdField: ResourceSoftwareIdField;
}): EmptyProjectionDetails.WithProjectionData => {
    const softwareId = projectionDetail[resourceSoftwareIdField];
    const projection = projectionsByResourceId?.[softwareId!];
    const projectionFields = projection
        ? {
              [PROJECTION_ATTERRISSAGE_STATUS]: projection?.atterrissageStatus,
              [PROJECTION_PLURIANNUAL_STATUS]:
                  projection?.pluriannualProjectionStatus,
              [PROJECTION_ID]: projection?.id,
          }
        : {};
    return {
        ...projectionDetail,
        ...projectionFields,
    };
};

// FIXME: update this function to work with any number of dimension
/**
 * Add projection fields to projection details, if they exist
 */
export const getProjectionDetailsWithProjectionFields = ({
    projectionDetailsData,
    projectionsByResourceId,
    resourceSoftwareIdField,
}: {
    projectionDetailsData:
        | (
              | EmptyProjectionDetails.FromEndpoint
              | AtterrissageProjectionDetails.FromEndpoint
              | PluriannualProjectionDetails.FromEndpoint
          )[]
        | undefined;
    projectionsByResourceId: Record<string, ProjectionT> | undefined;
    resourceSoftwareIdField: ResourceSoftwareIdField;
}): EmptyProjectionDetails.WithProjectionData[] | undefined => {
    const hasProjections =
        projectionsByResourceId &&
        Object.keys(projectionsByResourceId).length > 0;
    const newProjectionDetailsData = hasProjections
        ? projectionDetailsData?.map((projectionDetail) =>
              addProjectionFieldsToProjectionDetails({
                  projectionDetail,
                  projectionsByResourceId,
                  resourceSoftwareIdField,
              }),
          )
        : projectionDetailsData;
    return newProjectionDetailsData;
};

export const useGetSingleGridFilter = (
    projectionDetails: EmptyProjectionDetails.WithMetadata[],
    filterState: { [ventilation: string]: SelectionTreeT[] },
    setFiltersState: (value: {
        [ventilation: string]: SelectionTreeT[];
    }) => void,
    projectionName: ProjectionVentilation.Labels,
    onFilteredTrackEvent?: string,
) => {
    const uniqueValues = Array.from(
        new Set(
            projectionDetails
                ?.map((detail) => detail[projectionName])
                .filter((el) => el !== null),
        ),
    );

    const setSelectedValues = useCallback(
        (values: SelectionTreeT[]) => {
            const newFilterState = {
                ...filterState,
                [projectionName]: values,
            };
            setFiltersState(newFilterState);
            if (onFilteredTrackEvent) {
                trackEventBudget(onFilteredTrackEvent, {
                    resource: projectionName,
                });
            }
        },
        [JSON.stringify(filterState), setFiltersState, projectionName],
    );

    return {
        selectedValues: filterState[projectionName],
        setSelectedValues,
        uniqueValues,
    };
};

/**
 * Check if provided value is greater than the max value allowed for the amount input
 * and return the max value if it is the case
 */
const getMaxAmountValue = (value: number): number =>
    // arbitrary max value for the amount input to avoid PSQL out of range, and still provide a hint to the user
    {
        const MAX_INT = 999999999;
        return value > MAX_INT ? MAX_INT : value;
    };

export function getNewFormattedValue(
    newValue: string,
    defaultValue: string | undefined,
    field: 'comment',
): string;
export function getNewFormattedValue(
    newValue: string | number,
    defaultValue: number | undefined,
    field: 'amount',
): number;
export function getNewFormattedValue(
    newValue: string | number,
    defaultValue: string | number | undefined,
    field: 'amount' | 'comment',
): string | number;
export function getNewFormattedValue(
    newValue: string | number,
    defaultValue: string | number | undefined,
    field: 'amount' | 'comment',
) {
    let finalValue = newValue;
    if (!finalValue && defaultValue !== undefined) {
        // Product requirement: we need to replace blanks by 0 for numeric columns
        finalValue = defaultValue;
    }
    if (field === 'amount') {
        const numberValue =
            typeof finalValue === 'string'
                ? parseNumericInputOrDefault(finalValue)
                : finalValue;
        finalValue = getMaxAmountValue(numberValue);
    }
    return finalValue;
}

/**
 * Returns a mapping of ventilations with actual entities values (resources names etc.)
 */
export const getProjectionsDimensionMapping = (
    resourceSoftwareIdField: ResourceSoftwareIdField,
    projectionBySoftwareIdArray: ProjectionVentilation.SoftwareIds[],
    ventilationsLabelMapping: VentilationsLabelMapping | undefined,
    projectionDetailsWithProjections: AbstractProjectionDetailsT[] | undefined,
): ProjectionsDimensionsMapping =>
    [resourceSoftwareIdField, ...projectionBySoftwareIdArray].reduce(
        (mapping, ventilation) => {
            if (!ventilationsLabelMapping) {
                return { ...mapping };
            }
            const name = ventilationsLabelMapping[
                ventilation
            ] as ProjectionVentilation.Labels;
            return {
                ...mapping,
                ...getDimensionMapping(
                    projectionDetailsWithProjections || [],
                    name,
                    ventilation,
                ),
            };
        },
        {},
    );

export const getVentilationFromNode = (
    node: IRowNode,
): ProjectionVentilationData => {
    // in case grouped value is null, node.key is empty string, so we need to get the actual value from the underlying data.
    if (node.allLeafChildren?.length === 0) {
        return {} as ProjectionVentilationData;
    }
    const field = node.field as ProjectionVentilation.SoftwareIds;
    const ventilation = {
        [field]: node.allLeafChildren?.[0].data[field],
    } as ProjectionVentilationData;
    if (node.parent && node.parent?.key !== null) {
        return { ...ventilation, ...getVentilationFromNode(node.parent) };
    }
    return ventilation;
};
